// Version: 1.2
// v1.2: A device whose CVEs are vendor patched (app.py v4.41: the edge NAS while DSM is
//       up to date) shows them in grey, labelled Vendor patched, with a line saying which
//       DSM is installed and when it was checked. The CVEs stay listed; they are not
//       counted in the IT report grade.
// v1.1: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v1.0: Known-vulnerability badge and list for edge-discovered devices
//       (backlog CV-29). The API only sends cves / cve_summary to STG admins,
//       so these render nothing for managers and clients. Shared by
//       ClientDetail.js (Device Hub), ClientManagement.js (Discovered Devices)
//       and NetworkDevices.js (Network page).
import React, { useState } from 'react';

const RED = '#ff2d55';
const AMBER = '#ff9f0a';
const GREY = '#86868b';

function fmtChecked(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT';
}

// v1.2: one line explaining why a vendor-patched device's CVEs don't count
function patchedText(vp) {
  const when = fmtChecked(vp.checked_at);
  const how = vp.method === 'nas' ? 'the NAS' : "Synology's update list";
  return `${vp.vendor || 'The vendor'} patches the software bundled in DSM without changing the version the scan sees. `
    + `${vp.version || 'DSM'} is the latest available${when ? ` (checked by ${how}, ${when})` : ''}, `
    + 'so these are not counted. They count again if a DSM update is waiting.';
}

function cveColor(cvss) {
  return cvss >= 9 ? RED : AMBER;
}

// Small badge for a device's top-right badge column: "3 CVEs", red if any critical.
export function HostCveBadge({ host, onClick }) {
  const s = host && host.cve_summary;
  if (!s || !(host.cves || []).length) return null;
  const n = host.cves.length;
  const vp = host.vendor_patched;
  const color = vp ? GREY : s.critical ? RED : AMBER;
  const title = (vp ? 'Vendor patched: ' : '') + `${s.critical} critical, ${s.high} high` + (s.exploited ? `, ${s.exploited} known to be exploited` : '');
  return (
    <span title={title} onClick={onClick} style={{
      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
      background: `${color}20`, border: `1px solid ${color}60`, color,
      cursor: onClick ? 'pointer' : 'default', whiteSpace: 'nowrap',
    }}>
      {vp ? '' : s.exploited ? '⚠ ' : '! '}{n} CVE{n !== 1 ? 's' : ''}{vp ? ' · vendor patched' : ''}
    </span>
  );
}

// Dot marking an individual service tag that has CVEs.
export function ServiceCveDot({ service }) {
  if (!service || !(service.cves || []).length) return null;
  return (
    <span title={`${service.cves.length} known vulnerabilit${service.cves.length !== 1 ? 'ies' : 'y'}: ${service.cves.join(', ')}`}
      style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 3, background: service.vendor_patched ? GREY : RED, marginLeft: 4, verticalAlign: 'middle' }} />
  );
}

// Collapsible list of a device's CVEs. Starts closed unless `open` is passed.
export function HostCveList({ host, open: openProp }) {
  const [open, setOpen] = useState(!!openProp);
  const cves = (host && host.cves) || [];
  if (!cves.length) return null;
  const s = host.cve_summary || {};
  const vp = host.vendor_patched;
  const tint = (c) => vp ? GREY : cveColor(c);
  return (
    <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
      <button className="btn btn-quiet btn-sm" onClick={() => setOpen(o => !o)} style={{ color: vp ? GREY : s.critical ? RED : AMBER }}>
        {open ? '▾' : '▸'} Known vulnerabilities ({cves.length}){vp ? ', vendor patched' : ''}
      </button>
      {vp && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>{patchedText(vp)}</div>
      )}
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
          {cves.map(c => (
            <div key={c.id} style={{
              borderLeft: `3px solid ${tint(c.cvss)}`, padding: '4px 8px',
              background: 'var(--surface-2, rgba(127,127,127,0.06))', borderRadius: 4,
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 11 }}>
                <a href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(c.id)}`} target="_blank" rel="noopener noreferrer"
                  style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: tint(c.cvss), textDecoration: 'none' }}>
                  {c.id}
                </a>
                <span style={{ color: 'var(--text-muted)' }}>CVSS {Number(c.cvss).toFixed(1)}</span>
                {c.exploited && (
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: `${RED}20`, border: `1px solid ${RED}60`, color: RED }}>
                    KNOWN EXPLOITED
                  </span>
                )}
                <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {c.product || 'service'}{c.port ? ` · port ${c.port}` : ''}
                </span>
              </div>
              {c.description && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>{c.description}</div>
              )}
            </div>
          ))}
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
            Matched by product version from the network scan. Some systems patch without changing the version number, so confirm before acting.
          </div>
        </div>
      )}
    </div>
  );
}
